import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardBody, Col, Spinner, Badge, Modal, ModalHeader, ModalBody, Table } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import FilterComponent from "../../Components/Tables/DataTables/Common/FilterComponent";
import { Btn } from "../../AbstractElements";
import axios from "axios";
import { baseUrl } from "../../Utils/FileUpload";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEdit, FaTrash, FaShieldAlt } from "react-icons/fa";
import { useAuth } from "../../Context/AuthContext";

interface PermissionItem {
  id: number;
  slug: string;
  section: string;
  module: string;
  action: string;
}

interface RoleItem {
  id: number;
  name: string;
  slug: string;
  description: string;
  permissions_count?: number;
  permissions?: PermissionItem[];
  created_at: string;
}

const customStyles = {
  headCells: {
    style: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#308e87",
      backgroundColor: "#f8f9fa",
      borderBottom: "2px solid #308e87",
    },
  },
  rows: { style: { borderBottom: "1px solid #dee2e6" } },
  table: { style: { border: "1px solid #dee2e6", borderRadius: "8px" } },
};

export default function ViewRoles() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterText, setFilterText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Modal State for Viewing Role Permissions
  const [viewModal, setViewModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RoleItem | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filterText), 350);
    return () => clearTimeout(timer);
  }, [filterText]);

  const fetchRoles = useCallback(
    async (page = currentPage, pp = perPage) => {
      setLoading(true);
      try {
        const response = await axios.post(
          `${baseUrl}role/get_all`,
          {
            page,
            per_page: pp,
            search: debouncedSearch.trim() || undefined,
            sort_by: "created_at",
            sort_direction: "desc",
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setRoles(response.data.data || []);
        setTotalRows(response.data.total || 0);
        setCurrentPage(response.data.current_page || 1);
      } catch (error) {
        console.error("Error fetching roles:", error);
        toast.error("Failed to fetch roles.");
      } finally {
        setLoading(false);
      }
    },
    [currentPage, perPage, debouncedSearch, token]
  );

  useEffect(() => {
    fetchRoles(currentPage, perPage);
  }, [currentPage, perPage, debouncedSearch]);

  const handleDelete = useCallback(
    (roleId: number, roleName: string) => {
      Swal.fire({
        title: "Are you sure?",
        text: `Do you really want to delete role "${roleName}"?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await axios.post(
              `${baseUrl}role/delete`,
              { id: roleId },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success("Role deleted successfully.");
            fetchRoles(currentPage, perPage);
          } catch (error: any) {
            toast.error(error.response?.data?.message || "Failed to delete role.");
          }
        }
      });
    },
    [token, currentPage, perPage, fetchRoles]
  );

  const handleOpenViewModal = (role: RoleItem) => {
    setSelectedRole(role);
    setViewModal(true);
  };

  // Group permissions for modal directly by module (single table)
  const groupedModalPermissions = useMemo(() => {
    if (!selectedRole || !selectedRole.permissions) return {};
    const grouped: Record<string, string[]> = {};

    selectedRole.permissions.forEach((perm) => {
      const module = perm.module || "Other";
      if (!grouped[module]) grouped[module] = [];
      grouped[module].push(perm.action);
    });

    return grouped;
  }, [selectedRole]);

  const columns = useMemo(
    () => [
      {
        name: <div>Sl.No</div>,
        cell: (_: any, index: number) => (currentPage - 1) * perPage + index + 1,
        width: "75px",
      },
      {
        name: <div>Role Name</div>,
        cell: (row: RoleItem) => (
          <strong style={{ color: "#308e87", fontSize: "15px" }}>{row.name}</strong>
        ),
        sortable: true,
      },
      {
        name: <div>Description</div>,
        cell: (row: RoleItem) => row.description || "—",
      },
      {
        name: <div>Permissions Count</div>,
        cell: (row: RoleItem) => (
          <Badge pill color="info" style={{ fontSize: "13px", padding: "6px 12px" }}>
            {row.permissions_count ?? row.permissions?.length ?? 0} Permissions
          </Badge>
        ),
      },
      {
        name: <div>Actions</div>,
        cell: (row: RoleItem) => (
          <div className="d-flex gap-2">
            {hasPermission("role-management.view") && (
              <Btn
                color="info"
                size="sm"
                className="p-1"
                title="View Role"
                onClick={() => handleOpenViewModal(row)}
                style={{ width: "30px", height: "30px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
              >
                <FaEye size={14} />
              </Btn>
            )}
            {hasPermission("role-management.edit") && (
              <Btn
                color="warning"
                size="sm"
                className="p-1"
                title="Edit Role"
                onClick={() => navigate("/edit_role", { state: { id: row.id } })}
                style={{ width: "30px", height: "30px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
              >
                <FaEdit size={14} />
              </Btn>
            )}
            {hasPermission("role-management.delete") && (
              <Btn
                color="danger"
                size="sm"
                className="p-1"
                title="Delete Role"
                onClick={() => handleDelete(row.id, row.name)}
                style={{ width: "30px", height: "30px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
              >
                <FaTrash size={14} />
              </Btn>
            )}
          </div>
        ),
      },
    ],
    [currentPage, perPage, navigate, handleDelete, hasPermission]
  );

  return (
    <div className="page-body">
      <Breadcrumbs pageTitle="Role Management" parent="Master Settings" title="Roles" />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody>
            {hasPermission("role-management.add") && (
              <Col xs={12} className="mt-2 d-flex justify-content-end mb-3">
                <Btn color="primary" onClick={() => navigate("/add_role")}>
                  Add
                </Btn>
              </Col>
            )}
            {hasPermission("role-management.view") && (
              <>
                <div className="mb-3">
                  <FilterComponent
                    onFilter={(e: any) => {
                      setFilterText(e.target.value);
                      setCurrentPage(1);
                    }}
                    filterText={filterText}
                  />
                </div>

                <div className="table-responsive custom-scrollbar">
                  <DataTable
                    data={roles}
                    columns={columns}
                    highlightOnHover
                    striped
                    customStyles={customStyles}
                    pagination
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={totalRows}
                    onChangePage={(p) => setCurrentPage(p)}
                    onChangeRowsPerPage={(np) => {
                      setPerPage(np);
                      setCurrentPage(1);
                    }}
                    paginationDefaultPage={currentPage}
                    responsive
                    progressPending={loading}
                    progressComponent={
                      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px", width: "100%" }}>
                        <Spinner color="primary" />
                      </div>
                    }
                  />
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </Col>

      {/* View Role Permissions Modal */}
      <Modal isOpen={viewModal} toggle={() => setViewModal(false)} size="lg" centered>
        <ModalHeader
          toggle={() => setViewModal(false)}
          style={{ background: "linear-gradient(135deg, #308e87 0%, #1f5e59 100%)", color: "#fff" }}
        >
          <div className="d-flex align-items-center gap-2" style={{ color: "#fff" }}>
            <FaShieldAlt size={20} />
            <span>Role Permissions: {selectedRole?.name}</span>
          </div>
        </ModalHeader>
        <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {selectedRole && (
            <div>
              <p className="mb-3">
                <strong>Description:</strong> {selectedRole.description || "N/A"}
              </p>
              {Object.keys(groupedModalPermissions).length === 0 ? (
                <p className="text-muted">No permissions assigned to this role.</p>
              ) : (
                <Table size="sm" bordered striped responsive>
                  <thead>
                    <tr style={{ backgroundColor: "#f8f9fa" }}>
                      <th style={{ width: "35%", color: "#308e87" }}>Module</th>
                      <th style={{ color: "#308e87" }}>Assigned Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(groupedModalPermissions).map(([moduleName, actions]) => (
                      <tr key={moduleName}>
                        <td className="font-weight-bold">{moduleName}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {actions.map((act) => (
                              <Badge key={act} color="success" pill style={{ padding: "5px 10px", fontSize: "11px" }}>
                                {act === "enable_disable"
                                  ? "Enable / Disable"
                                  : act === "download_pdf"
                                    ? "Download PDF"
                                    : act === "download_excel"
                                      ? "Download Excel"
                                      : ucwords(act)}
                              </Badge>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          )}
        </ModalBody>
      </Modal>

      <ToastContainer />
    </div>
  );
}

function ucwords(str: string) {
  return str.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}
